import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import type { UIElement } from '@adyen/adyen-web';
import type { AdyenGooglePayHandlers } from '@/types/adyen';
import { CheckoutContext } from '@/context/CheckoutContext';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import type { AdyenGooglePayConfig, AdyenGooglePayPaymentMethod } from '@/types/adyen';
import { adyenExpressService } from '@/services/adyenExpressService';
import type { AdyenFinalizePaymentResponse } from '@/types/adyen';
import type { ExpressCheckoutComponentProps } from '@/utils/expressPayment';
import { Errors } from '@/constants/errors';

export interface UseAdyenGooglePayOptions extends ExpressCheckoutComponentProps {
  onClick?: AdyenGooglePayHandlers['onClick'];
}

export function useAdyenGooglePay({ onReady, onClick }: UseAdyenGooglePayOptions = {}) {
  const {
    paymentMethodsSettings,
    pricing,
    customer,
    actions: { runPayment, setError },
  } = useContext(CheckoutContext);

  const [googlePayElement, setGooglePayElement] = useState<UIElement | null>(null);
  const [error, setErrorState] = useState<Error | null>(null);
  const isPaymentInProgress = useRef(false);
  const submitParamsRef = useRef<{ email: string }>({ email: '' });

  const googlePayPaymentMethod = useMemo(
    () =>
      paymentMethodsSettings.find(
        (pm) => pm.paymentMethodName === PaymentMethodEnum.GOOGLE_PAY && pm.provider === ProviderEnum.ADYEN
      ) as AdyenGooglePayPaymentMethod | undefined,
    [paymentMethodsSettings]
  );

  const handleError = useCallback(() => {
    // ACDEV-22923: uncomment when error handling is implemented
    // setErrorState(err instanceof Error ? err : new Error('Google Pay error'));
    // setError?.('somethingWentWrong');
    isPaymentInProgress.current = false;
  }, [setError]);

  useEffect(() => {
    if (!googlePayPaymentMethod) {
      onReady?.(false);
      return;
    }
    let googlePayInstance: UIElement | null = null;

    const initGooglePay = async () => {
      try {
        await adyenExpressService.initializeCheckoutInstance(
          googlePayPaymentMethod.publicConfig as AdyenGooglePayConfig,
          customer ?? undefined,
          googlePayPaymentMethod.paymentMethodConfig as Record<string, unknown> | undefined
        );

        const handlers: AdyenGooglePayHandlers = {
          ...(onClick && { onClick }),
          onAuthorized: (data, actions) => {
            submitParamsRef.current.email = data?.authorizedEvent?.email ?? customer?.email ?? '';
            actions.resolve();
          },
          onError: (err) => {
            console.error('Adyen Google Pay onError:', err);
            handleError();
          },
          onPaymentFailed: () => {
            handleError();
          },
          onSubmit: async (state, _, actions) => {
            if (isPaymentInProgress.current) return;
            isPaymentInProgress.current = true;
            adyenExpressService.setGooglePayActions(actions);

            try {
              await runPayment<AdyenFinalizePaymentResponse>(
                {
                  paymentMethod: state.data.paymentMethod,
                  browserInfo: state.data.browserInfo,
                  email: submitParamsRef.current.email || customer?.email,
                },
                PaymentMethodEnum.GOOGLE_PAY,
                ProviderEnum.ADYEN
              );
            } catch (err) {
              handleError();
              adyenExpressService.completeGooglePayPayment(false);
            } finally {
              isPaymentInProgress.current = false;
            }
          },
        };

        const component = await adyenExpressService.createGooglePayComponent({
          googlePayPaymentMethod,
          customer,
          pricing,
          handlers,
        });

        if (!component) return;

        googlePayInstance = component;
        setGooglePayElement(googlePayInstance);
        await component.isAvailable();
        onReady?.(true);
      } catch (err) {
        console.error('Adyen Google Pay initialization error:', err);
        setErrorState(err instanceof Error ? err : new Error(Errors.payment.failedToInitializeGooglePay));
        onReady?.(false);
      }
    };

    initGooglePay();

    return () => {
      googlePayInstance?.unmount();
    };
  }, [pricing]);

  return {
    googlePayElement,
    error,
  };
}
